import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { StructureSet } from '~/domain/patient/structureset';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useActivateRegistration } from '~/hooks/registration/useRegistrationStore';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { useReferenceRow } from '~/hooks/viewer/useReferenceRow';
import { selectedToolState } from '~/state/atoms/toolsAtoms';
import { useReviewDatasets } from './useReviewDatasets';
import { useRunDeformation } from './useRunDeformation';
import { useRunRigidRegistration } from './useRunRigidRegistration';
import { useRunScans } from './useRunScans';
import { notifyReferenceRowRefused } from './notifyReferenceRowRefused';

// The run's planning scan in a row below the target, tied through its rigid; open with the landmark tool or always.
export function useRunLandmarkComparison(
    alwaysOpen = false,
    secondarySet?: StructureSet
): void {
    const patient = useSelectedPatient();
    const selectedTool = useRecoilValue(selectedToolState);
    const primaryImage = useSelectedPrimaryImage();
    const { planning, target } = useRunScans();
    const deformation = useRunDeformation();
    // The caller's set on the planning row, else the run's source set: the row never opens empty.
    const { sourceSet } = useReviewDatasets();
    const secondary = secondarySet ?? sourceSet;
    const { setEnvironmentPrimaryImage } = useViewportEnvironment();
    const referenceRow = useReferenceRow();
    const activateRegistration = useActivateRegistration();
    const [waitingForTarget, setWaitingForTarget] = useState(false);
    const openedFor = useRef<string | undefined>();
    const shownFor = useRef<string | undefined>();

    const rigid = useRunRigidRegistration();

    // Held, not depended on: these hooks return a fresh object every render.
    const apply = useRef({
        setEnvironmentPrimaryImage,
        referenceRow,
        activateRegistration,
        secondary,
        planning,
    });
    useEffect(() => {
        apply.current = {
            setEnvironmentPrimaryImage,
            referenceRow,
            activateRegistration,
            secondary,
            planning,
        };
    });

    const shouldShow = selectedTool === 'landmark' || alwaysOpen;
    const runKey =
        planning && target && rigid && deformation
            ? `${planning.seriesUid}|${target.seriesUid}|${deformation.registration.sopInstanceUid}`
            : undefined;

    // Once per activation: the target first becomes the primary scan, the field's fixed frame.
    useEffect(() => {
        if (!shouldShow) {
            const shownPlanning = apply.current.planning;
            if (shownFor.current && shownPlanning) {
                apply.current.referenceRow.end(shownPlanning);
            }
            openedFor.current = undefined;
            shownFor.current = undefined;
            setWaitingForTarget(false);
            return;
        }
        if (!runKey || !patient || !target || openedFor.current === runKey) {
            return;
        }
        openedFor.current = runKey;
        if (primaryImage?.seriesUid !== target.seriesUid) {
            apply.current.setEnvironmentPrimaryImage(
                [target],
                patient.dataSet.PatientID
            );
        }
        setWaitingForTarget(true);
    }, [shouldShow, runKey, patient, target, primaryImage]);

    useEffect(() => {
        if (
            !waitingForTarget ||
            !runKey ||
            shownFor.current === runKey ||
            !planning ||
            !target ||
            !rigid ||
            !deformation ||
            primaryImage?.seriesUid !== target.seriesUid
        ) {
            return;
        }
        shownFor.current = runKey;
        setWaitingForTarget(false);
        if (
            !apply.current.referenceRow.show(planning, rigid, {
                structureSet: apply.current.secondary,
            })
        ) {
            notifyReferenceRowRefused();
        }
        apply.current.activateRegistration(
            deformation.registration.sopInstanceUid,
            planning.seriesUid
        );
    }, [
        waitingForTarget,
        runKey,
        planning,
        target,
        rigid,
        deformation,
        primaryImage,
    ]);

    // The planning row's structures can settle after it opens; keep it on the current set.
    useEffect(() => {
        if (shownFor.current && planning && rigid) {
            apply.current.referenceRow.show(planning, rigid, {
                structureSet: secondary,
            });
        }
    }, [secondary, planning, rigid]);

    useEffect(
        () => () => {
            const shownPlanning = apply.current.planning;
            if (shownFor.current && shownPlanning) {
                apply.current.referenceRow.end(shownPlanning);
            }
        },
        []
    );
}
